/**
 * /admin/audit-events - audit log viewer with filters + CSV export.
 *
 * Per CHAP-174 (Phase 1N #146). Admin-only.
 */

import { Fragment, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api.js';

interface AuditEventRow {
  id: string;
  tenantId: string | null;
  tenantSlug: string | null;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  targetTable: string | null;
  targetId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  ua: string | null;
  ts: string;
}

interface AuditResponse {
  events: AuditEventRow[];
  nextCursor: string | null;
  count: number;
  hasMore: boolean;
}

export default function AdminAuditLog() {
  const [tenant, setTenant] = useState('');
  const [action, setAction] = useState('');
  const [targetTable, setTargetTable] = useState('');
  const [actorEmail, setActorEmail] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (tenant) p.set('tenant', tenant);
    if (action) p.set('action', action);
    if (targetTable) p.set('targetTable', targetTable);
    if (actorEmail) p.set('q', actorEmail);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    p.set('limit', '100');
    return p.toString();
  }, [tenant, action, targetTable, actorEmail, from, to]);

  const { data, isLoading, error } = useQuery<AuditResponse, Error>({
    queryKey: ['admin-audit', queryString],
    queryFn: () => apiGet<AuditResponse>(`/api/admin/audit-events?${queryString}`),
  });

  const csvUrl = `/api/admin/audit-events.csv?${queryString}`;

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Audit Log</h2>
            <p className="text-neutral text-sm mt-1">
              All audited actions across tenants. Per CHAP-174.
            </p>
          </div>
          <a
            href={csvUrl}
            download
            className="text-sm font-medium px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 flex-shrink-0"
          >
            Export CSV
          </a>
        </div>
      </header>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h3 className="text-navy text-sm font-semibold mb-3">Filters</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
          <Field label="Tenant slug">
            <input
              type="text"
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              placeholder="omega-zeta"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Action">
            <input
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="dataroom_document_metadata_edit"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Target table">
            <input
              type="text"
              value={targetTable}
              onChange={(e) => setTargetTable(e.target.value)}
              placeholder="dataroom_documents"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Actor email contains">
            <input
              type="text"
              value={actorEmail}
              onChange={(e) => setActorEmail(e.target.value)}
              placeholder="jake@"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </Field>
          <Field label="From (ISO date)">
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </Field>
          <Field label="To (ISO date)">
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </Field>
        </div>
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading && (
          <div className="p-6 text-center text-neutral text-sm">Loading events…</div>
        )}
        {error && (
          <div className="border-l-4 border-red-400 bg-red-50 text-red-800 text-sm p-3">
            Failed to load: {error.message}
          </div>
        )}
        {data && (
          <>
            <div className="px-4 py-2 border-b border-gray-100 text-xs text-neutral">
              {data.count} events{data.hasMore ? ' (more available - narrow filters)' : ''}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-neutral bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 font-medium">Timestamp</th>
                    <th className="px-3 py-2 font-medium">Tenant</th>
                    <th className="px-3 py-2 font-medium">Actor</th>
                    <th className="px-3 py-2 font-medium">Action</th>
                    <th className="px-3 py-2 font-medium">Target</th>
                    <th className="px-3 py-2 font-medium">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-neutral">
                        No events match the filters.
                      </td>
                    </tr>
                  )}
                  {data.events.map((e) => {
                    const expanded = expandedId === e.id;
                    const hasDiff = e.before != null || e.after != null;
                    return (
                      <Fragment key={e.id}>
                        <tr
                          className={`border-b border-gray-50 hover:bg-gray-50 ${hasDiff ? 'cursor-pointer' : ''}`}
                          onClick={() => hasDiff && setExpandedId(expanded ? null : e.id)}
                        >
                          <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                            {hasDiff && (
                              <span className="text-neutral mr-1 select-none">{expanded ? '▾' : '▸'}</span>
                            )}
                            {new Date(e.ts).toLocaleString()}
                          </td>
                          <td className="px-3 py-1.5 font-mono">{e.tenantSlug ?? 'n/a'}</td>
                          <td className="px-3 py-1.5 truncate max-w-[180px]">
                            {e.actorEmail ?? 'n/a'}
                          </td>
                          <td className="px-3 py-1.5 font-mono">{e.action}</td>
                          <td className="px-3 py-1.5 font-mono">
                            {e.targetTable ?? 'n/a'}
                            {e.targetId && (
                              <span className="text-neutral">/{e.targetId.slice(0, 8)}</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 font-mono">{e.ip ?? 'n/a'}</td>
                        </tr>
                        {expanded && (
                          <tr className="bg-gray-50/60">
                            <td colSpan={6} className="px-3 py-2">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <div className="text-neutral mb-1 font-medium">Before</div>
                                  <pre className="overflow-x-auto rounded border border-gray-100 bg-white p-2 text-[11px] leading-snug">
                                    {e.before != null ? JSON.stringify(e.before, null, 2) : 'n/a'}
                                  </pre>
                                </div>
                                <div>
                                  <div className="text-neutral mb-1 font-medium">After</div>
                                  <pre className="overflow-x-auto rounded border border-gray-100 bg-white p-2 text-[11px] leading-snug">
                                    {e.after != null ? JSON.stringify(e.after, null, 2) : 'n/a'}
                                  </pre>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="block text-xs font-medium text-navy mb-1">{label}</span>
      {children}
    </label>
  );
}
